import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Hospital, Building2, GraduationCap, Landmark, Plane, Dumbbell, Factory,
    ShoppingBag, Shield, HeartPulse, ArrowRight, CheckCircle2,
} from "lucide-react";

const INDUSTRIES = [
    { key: "healthcare", icon: Hospital, accent: "from-red-500 to-orange-500" },
    { key: "corporate", icon: Building2, accent: "from-orange-500 to-amber-500" },
    { key: "education", icon: GraduationCap, accent: "from-red-600 to-rose-500" },
    { key: "government", icon: Landmark, accent: "from-orange-600 to-red-500" },
    { key: "transport", icon: Plane, accent: "from-amber-500 to-orange-500" },
    { key: "fitness", icon: Dumbbell, accent: "from-red-500 to-pink-500" },
    { key: "mining", icon: Factory, accent: "from-orange-600 to-amber-600" },
    { key: "retail", icon: ShoppingBag, accent: "from-rose-500 to-red-500" },
    { key: "insurance", icon: Shield, accent: "from-red-500 to-orange-600" },
];

export default function Industries() {
    const { t } = useTranslation();

    return (
        <div className="bg-white min-h-screen" data-testid="industries-page">
            <Navbar />
            <main className="pt-28 pb-20">
                {/* ---------- Hero ---------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 pb-14">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-4">
                        {t("industries.eyebrow")}
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter leading-[0.95] mb-5">
                        {t("industries.titleLine1")} <br />
                        <span className="brand-gradient-text">{t("industries.titleLine2")}</span>
                    </h1>
                    <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
                        {t("industries.intro")}
                    </p>
                </section>

                {/* ---------- Industry cards grid ---------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
                    {INDUSTRIES.map((ind, i) => {
                        const Icon = ind.icon;
                        return (
                            <article
                                key={ind.key}
                                className="group relative card-soft p-7 overflow-hidden"
                                data-testid={`industry-${ind.key}`}
                            >
                                <div className={`absolute -right-16 -top-16 w-48 h-48 rounded-full bg-gradient-to-br ${ind.accent} opacity-0 group-hover:opacity-15 blur-2xl transition-opacity`} />
                                <div className="relative">
                                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${ind.accent} grid place-items-center mb-5 text-white shadow-lg`}>
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <div className="font-display text-xl md:text-2xl font-semibold mb-2">
                                        {t(`industries.items.${ind.key}.title`)}
                                    </div>
                                    <p className="text-sm text-gray-600 leading-relaxed mb-5">
                                        {t(`industries.items.${ind.key}.desc`)}
                                    </p>
                                    <ul className="space-y-2">
                                        {[1, 2, 3].map((n) => {
                                            const b = t(`industries.items.${ind.key}.b${n}`);
                                            if (!b || b.startsWith("industries.")) return null;
                                            return (
                                                <li key={n} className="text-xs text-gray-700 flex gap-2 items-start">
                                                    <CheckCircle2 className="w-4 h-4 text-[var(--medx-red)] shrink-0 mt-0.5" />
                                                    <span>{b}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </article>
                        );
                    })}
                </section>

                {/* ---------- Impact stats strip ---------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
                    <div className="rounded-3xl bg-gradient-to-br from-[#0b0e1a] to-[#1e293b] text-white p-10 md:p-14 relative overflow-hidden">
                        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[var(--medx-orange)]/20 blur-3xl" />
                        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-10">
                            <Stat value="180s" label={t("industries.stats.scanTime")} />
                            <Stat value="30+" label={t("industries.stats.vitals")} />
                            <Stat value="8" label={t("industries.stats.languages")} />
                            <Stat value="100%" label={t("industries.stats.recordOwnership")} />
                        </div>
                    </div>
                </section>

                {/* ---------- CTA ---------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-red-50 p-10 md:p-14 grain">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-8">
                                <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">
                                    {t("industries.cta.eyebrow")}
                                </div>
                                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mb-3">
                                    {t("industries.cta.title")}
                                </h2>
                                <p className="text-gray-600 text-base leading-relaxed">
                                    {t("industries.cta.p1")}
                                </p>
                            </div>
                            <div className="lg:col-span-4 flex flex-col gap-3">
                                <Link to="/order" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="industries-order-cta">
                                    <HeartPulse className="w-4 h-4" /> {t("industries.cta.order")}
                                </Link>
                                <Link to="/contact" className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="industries-contact-cta">
                                    {t("industries.cta.contact")} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

function Stat({ value, label }) {
    return (
        <div>
            <div className="font-display text-5xl md:text-6xl font-bold brand-gradient-text leading-none">
                {value}
            </div>
            <div className="text-xs md:text-sm text-white/70 mt-3 leading-tight">{label}</div>
        </div>
    );
}
